import type { z } from "zod";

import {
  positiveIntegerSchema,
  stringArraySchema,
  type TArticleFilters,
  type TFilters,
} from "./filters.schema";

/*-------------------------------------
|  Types
|-------------------------------------*/

export type { TArticleFilters, TFilters } from "./filters.schema";

export type TStoreFilterKey = {
  [TFilterGroup in keyof TFilters]: Extract<
    keyof TFilters[TFilterGroup],
    `${string}:${string}`
  >;
}[keyof TFilters];

export type TApiFilterKey<
  TFilter extends TStoreFilterKey = TStoreFilterKey,
> = TFilter extends `${string}:${infer TKey}` ? TKey : never;

export type TFilterConfig<TValue> = {
  defaultValue: TValue;
  schema: z.ZodType<TValue>;
};

type TFiltersConfig = {
  [TFilterGroup in keyof TFilters]: {
    [TFilter in keyof TFilters[TFilterGroup]]: TFilterConfig<
      TFilters[TFilterGroup][TFilter]
    >;
  };
};

export type TRuntimeFilterConfig = TFilterConfig<unknown>;

export type TFiltersStore = {
  filters: TFilters;
};

export type TFilterUpdateArgs = {
  [TFilterGroup in keyof TFilters]: {
    [TFilter in keyof TFilters[TFilterGroup]]: [
      filter: TFilter,
      value: TFilters[TFilterGroup][TFilter],
    ];
  }[keyof TFilters[TFilterGroup]];
}[keyof TFilters];

export type TFiltersStoreDispatch = {
  hydrateFilters(filters: Partial<TFilters>): void;
  updateFilters(...args: TFilterUpdateArgs): void;
};

export type TUseFiltersStore = {
  filters: TFilters;
  updateFilters(...args: TFilterUpdateArgs): void;
};

/*-------------------------------------
|  Factories
|-------------------------------------*/

const positiveIntegerFilter = (defaultValue = 1) => ({
  defaultValue,
  schema: positiveIntegerSchema,
});

const stringArrayFilter = (defaultValue: string[] = []) => ({
  defaultValue,
  schema: stringArraySchema,
});

/*-------------------------------------
|  Config
|-------------------------------------*/

/**
 * Prefix all filters with the client path,
 * this means that in future we can store the state for
 *  multiple on a single page if needed
 * */

export const FILTERS_CONFIG = {
  articles: {
    "articles:page": positiveIntegerFilter(),
    "articles:categories": stringArrayFilter(),
  },
} satisfies TFiltersConfig;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const DEFAULT_ARTICLE_FILTERS: TArticleFilters = {
  "articles:page": FILTERS_CONFIG.articles["articles:page"].defaultValue,
  "articles:categories": FILTERS_CONFIG.articles["articles:categories"].defaultValue,
};

export const DEFAULT_FILTERS: TFilters = {
  articles: DEFAULT_ARTICLE_FILTERS,
};
